// ── A draft that was EDITED keeps the draft card (busy-my-web#625) ─────────────────────────────
//
// "Write an email to my boss …" comes back as an `emailDraft` block: busy-intelligent-srv sets its
// `draft_email` intent and busy-main-bff turns that into the card (header, Subject, body, Copy /
// Write another draft / Use this draft). The intent is only set for "write / draft an email to …",
// though — the card's own "Write another draft" (`REGENERATE_DRAFT_QUESTION`) and a follow-up like
// "make it shorter" or "more casual" are answered as PROSE: the new email arrives inside a text
// block ("Here is a shorter version:\n\n**Subject:** …\n\nDear …"), and the card is gone.
//
// Until the server tags those as drafts too, the chat reads them back here: when the answer has no
// draft of its own, the question follows a draft (it is the regenerate, or the answer before it was
// a draft), and a text block holds an email (a "Subject:" line, or a "Dear …" / "Hi …" opening),
// that text becomes the lead sentence + the draft card (+ whatever the model said after the
// sign-off). The card keeps the draft's recipients and "To …" label; the tone label follows the
// question when it names a new tone. An answer that already carries a card is never touched.
//
// Pure — no store, no DOM — so the store calls it on every answered turn.

import { blockText } from '@/lib/answerBlocks'

/** The question the draft card's regenerate asks — a new draft, in the same conversation. */
export const REGENERATE_DRAFT_QUESTION = 'Regenerate the draft'

const str = (v) => (v === null || v === undefined ? '' : String(v))

// A line with its markdown dressing taken off: "**Subject:** Hi" → "Subject: Hi".
const plain = (line) => str(line).replace(/[*_#>`]/g, '').trim()

const SUBJECT = /^subject(?:\s+line)?\s*[:\-–—]\s*(.+)$/i
const GREETING = /^(dear|hi|hello|hey|good (morning|afternoon|evening)|greetings|to whom it may concern)\b/i
// A sign-off is the WHOLE line ("Best regards,") — "Thank you for your time." is body.
const SIGN_OFF = /^((((best|kind|warm|warmest)|with (best|kind|warm))\s+)?regards|(best|warm|warmest) wishes|sincerely( yours)?|yours (sincerely|truly|faithfully)|thanks( again)?|thank you|many thanks|cheers|best|respectfully|all the best)\s*[,.!]?$/i
// Fences and rules the model wraps an email in — never part of the email itself.
const WRAPPER = /^(```.*|-{3,}|\*{3,}|_{3,})$/

// Tone words a follow-up can ask for, and the label the card's header prints for each.
const TONES = [
  ['informal', 'Informal'], ['casual', 'Casual'], ['friendly', 'Friendly'], ['formal', 'Formal'],
  ['professional', 'Professional'], ['polite', 'Polite'], ['warm', 'Warm'], ['firm', 'Firm'],
  ['direct', 'Direct'], ['apologetic', 'Apologetic'], ['enthusiastic', 'Enthusiastic'],
]

/** Bold / italic markers out of a body line — the card prints plain text. */
const unmark = (line) => str(line).replace(/(\*\*|__)(.+?)\1/g, '$2')

/** Leading / trailing blank lines off a list of lines, joined. */
const joinTrimmed = (lines) => lines.join('\n').replace(/^\s*\n/, '').trim()

/**
 * The email inside a piece of prose, or null when there is none.
 *
 * An email starts at a "Subject:" line (any markdown around it) or, failing that, at a greeting
 * line ("Dear …", "Hi …"). It ends after the sign-off ("Best regards,") and the short lines under
 * it (the name), up to the next blank line; with no sign-off it runs to the end. What comes before
 * is the `lead`, what comes after is the `tail`.
 *
 * @returns {{ lead: string, subject: string, body: string, tail: string } | null}
 */
export function parseDraftText(markdown) {
  const lines = str(markdown).replace(/\r\n?/g, '\n').split('\n').filter((l) => !WRAPPER.test(l.trim()))
  let start = lines.findIndex((l) => SUBJECT.test(plain(l)))
  let subject = ''
  let bodyFrom
  if (start >= 0) {
    subject = plain(lines[start]).match(SUBJECT)[1].trim()
    bodyFrom = start + 1
  } else {
    start = lines.findIndex((l) => plain(l).length < 80 && GREETING.test(plain(l)))
    if (start < 0) return null
    bodyFrom = start
  }
  let end = lines.length
  const signOff = lines.findIndex((l, i) => i >= bodyFrom && SIGN_OFF.test(plain(l)))
  if (signOff >= 0) {
    end = signOff + 1
    while (end < lines.length && lines[end].trim()) end++
  }
  const body = joinTrimmed(lines.slice(bodyFrom, end).map(unmark))
  if (!body && !subject) return null
  return {
    lead: joinTrimmed(lines.slice(0, start)),
    subject,
    body,
    tail: joinTrimmed(lines.slice(end)),
  }
}

/** The tone a follow-up asks for ("make it more casual" → "Casual"; "less formal" names none), or '' when it names none. */
export function toneFromQuestion(question) {
  const q = str(question).toLowerCase()
  const hit = TONES.find(([word]) => new RegExp(`(?<!less )\\b${word}\\b`).test(q))
  return hit ? hit[1] : ''
}

const draftOf = (turn) => (Array.isArray(turn?.blocks) ? turn.blocks : []).find((b) => b && b.type === 'emailDraft') || null

/**
 * The draft a new question follows, or null.
 *
 * The regenerate is always about a draft, so it takes the latest one above it however far back;
 * any other question only follows a draft when the answer RIGHT above it was one ("make it
 * shorter" after anything else is not about an email). Turns still being asked are skipped.
 *
 * @param conversation the chat's turns, oldest first
 * @param turnId the turn being answered — only turns above it count (absent: all of them)
 */
export function previousDraft(conversation, turnId, question) {
  const turns = Array.isArray(conversation) ? conversation : []
  const at = turns.findIndex((t) => t && t.id === turnId)
  const above = (at >= 0 ? turns.slice(0, at) : turns).filter((t) => t && !t.asking).reverse()
  if (str(question).trim() === REGENERATE_DRAFT_QUESTION) {
    for (const t of above) {
      const d = draftOf(t)
      if (d) return d
    }
    return null
  }
  return draftOf(above[0])
}

/**
 * An answer's blocks with a prose email read back into a draft card (see the section note).
 *
 * Returned unchanged when the answer already has a draft, when there is no draft it follows, or
 * when no text block holds an email. Otherwise the first text block that does is replaced by its
 * lead sentence (if any), the `emailDraft` block, and the text after the email (if any) — each with
 * a key of its own derived from the text block's, and its `fallbackText`.
 */
export function draftFollowUpBlocks(blocks, { previous, question } = {}) {
  const list = Array.isArray(blocks) ? blocks : []
  if (!previous || list.some((b) => b && b.type === 'emailDraft')) return list
  const at = list.findIndex((b) => b && b.type === 'text' && parseDraftText(b.markdown))
  if (at < 0) return list
  const source = list[at]
  const parsed = parseDraftText(source.markdown)
  const text = (key, markdown) => ({ type: 'text', key, markdown, fallbackText: markdown })
  const draft = {
    type: 'emailDraft',
    key: `${source.key}-draft`,
    toLabel: previous.toLabel || null,
    toneLabel: toneFromQuestion(question) || previous.toneLabel || null,
    to: Array.isArray(previous.to) ? previous.to : [],
    subject: parsed.subject || previous.subject || '',
    body: parsed.body,
    replyToTimelineId: previous.replyToTimelineId || null,
  }
  draft.fallbackText = blockText(draft)
  const replaced = [
    parsed.lead ? text(`${source.key}-lead`, parsed.lead) : null,
    draft,
    parsed.tail ? text(`${source.key}-tail`, parsed.tail) : null,
  ].filter(Boolean)
  return [...list.slice(0, at), ...replaced, ...list.slice(at + 1)]
}
